import { call, put, takeLatest } from 'redux-saga/effects';
import * as types from './forecastActionType.js';
import * as actions from './forecastActions.js';
import * as services from './forecastServices.js';
import { SweetAlert } from '../../../../components/sweetalert/SweetAlert.js';
import { downloadBase64File } from '../../../../utils/fileDownloads';

// Fetch Forecast Reports Tab Data with Pagination
function* fetchForecastReportsTabSaga(action) {
    try {
        const response = yield call(services.getForecastReportsWithPagination, action.payload);
        yield put(actions.fetchForecastReportsTabSuccess(response));
    } catch (error) {
        yield put(actions.fetchForecastReportsTabFailure(error.message));
        SweetAlert.error({
            title: 'Error!',
            text: 'Failed to fetch Forecast data',
            showConfirmButton: true
        });
    }
}

// Delete Forecast Report Saga
function* deleteForecastReportSaga(action) {
    try {
        const ForecastDemandMasterId = action.payload.ForecastDemandMasterId;
        const ModifiedBy = action.payload.ModifiedBy;
        const response = yield call(services.deleteForecastReport, { ForecastDemandMasterId, ModifiedBy });
        
        if (response?.success) {
            yield put(actions.deleteForecastReportSuccess(response));
            SweetAlert.success({
                title: 'Deleted!',
                text: 'Forecast report has been deleted.',
                showConfirmButton: true,
            });

            // Refresh the reports table after delete with pagination params
            yield put(actions.fetchForecastReportsTabRequest({ SkipNoOfRecords: 0, perPage: 100, sSearch: '', sortField: 'ForecastDemandMasterId', sortOrder: 'desc' }));

            // Call meta.onSuccess if provided (for UI to handle dropdown and tab reset)
            if (action.meta && typeof action.meta.onSuccess === 'function') {
                yield action.meta.onSuccess();
            }
        } else {
            yield put(actions.deleteForecastReportFailure('Delete failed'));
            SweetAlert.error({
                title: 'Error!',
                text: response?.message || 'Failed to delete forecast report',
                showConfirmButton: true,
            });
        }
    } catch (error) {
        yield put(actions.deleteForecastReportFailure(error.message));
        SweetAlert.error({
            title: 'Error!',
            text: error.message || 'Failed to delete forecast report',
            showConfirmButton: true,
        });
    }
}

// Notes Edit Saga
function* fetchForecastNotesEditSaga(action) {
    try {
        const response = yield call(services.getForecastNotesEditData, action.payload);
        yield put(actions.fetchForecastNotesEditSuccess(response));
    } catch (error) {
        yield put(actions.fetchForecastNotesEditFailure(error.message));
        SweetAlert.error({
            title: 'Error!',
            text: 'Failed to fetch Notes edit data',
            showConfirmButton: true
        });
    }
}

// Notes Save Saga
function* fetchForecastNotesSaveSaga(action) {
    try {
        const response = yield call(services.saveForecastNotesData, action.payload);    
        yield put(actions.fetchForecastNotesSaveSuccess(response));
        SweetAlert.success({
            icon: 'success',
            title: 'Success',
            text: 'Notes saved successfully',
            showConfirmButton: true
        });
    } catch (error) {
        yield put(actions.fetchForecastNotesSaveFailure(error.message));
        SweetAlert.error({
            title: 'Error!',
            text: 'Failed to save Notes data',
            showConfirmButton: true
        });
    }
}

// Fetch All Dropdown Data for Forecasting Demand
function* fetchAllForecastingDemandDropdownsSaga(action) {
    try {
        const response = yield call(services.fetchAllForecastingDemandDropdowns, action.payload);
        if (response?.success) {
            yield put(actions.fetchAllForecastingDemandDropdownsSuccess(response.values || []));
        } else {
            yield put(actions.fetchAllForecastingDemandDropdownsFailure());
        }
    }
    catch (error) {
        yield put(actions.fetchAllForecastingDemandDropdownsFailure());
        SweetAlert.error({ title: 'Error!', text: 'Failed to load Worklist Dropdown Data' });
    }
}

function* fetchForecastingDemandByIdWithPaginationSaga(action) {
    try {
        const response = yield call(services.fetchForecastingDemandByIdWithPagination, action.payload);
        if (response?.success) {
            const { TabValue } = action.payload;
            const keyName = TabValue === 1 ? 'practiceDemandData' : 'siteDemandData';
            yield put(actions.fetchForecastingDemandByIdWithPaginationSuccess({ keyName, data: response || {} }));
        } else {
            yield put(actions.fetchForecastingDemandByIdWithPaginationFailure());
        }
    }
    catch (error) {
        yield put(actions.fetchForecastingDemandByIdWithPaginationFailure());
        SweetAlert.error({ title: 'Error!', text: 'Failed to load Forecasting Demand Data' });
    }
}

function* fetchForecastDemandSitePriorityDrillthroughSaga(action) {
    try {
        const response = yield call(services.fetchForecastDemandSitePriorityDrillThrough, action.payload);
        if (response?.success) {
            yield put(actions.fetchForecastDemandSitePriorityDrillthroughSuccess(response.values || []));
        } else {
            yield put(actions.fetchForecastDemandSitePriorityDrillthroughFailure());
            SweetAlert.error({ title: 'Error!', text: response?.message || 'Failed to download Site Priority Drillthrough data' });
        }
    }
    catch (error) {
        yield put(actions.fetchForecastDemandSitePriorityDrillthroughFailure());
        SweetAlert.error({ title: 'Error!', text: error.message || 'Failed to download Site Priority Drillthrough data' });
    }
}

function* saveForecastingDemandReportsSaga(action) {
    try {
        const response = yield call(services.saveForecastingDemandReports, action.payload);
        if (response?.success) {
            yield put(actions.SaveForecastingDemandReportsSuccess(response));
            SweetAlert.success({
                title: 'Success!',
                text: 'Forecasting Demand report has been saved.',
                showConfirmButton: true,
            });
        } else {
            yield put(actions.SaveForecastingDemandReportsFailure());
            SweetAlert.error({
                title: 'Error!',
                text: response?.message || 'Failed to save Forecasting Demand report',
                showConfirmButton: true,
            });
        }
    } catch (error) {
        yield put(actions.SaveForecastingDemandReportsFailure(error.message));
        SweetAlert.error({
            title: 'Error!',
            text: error.message || 'Failed to save Forecasting Demand report',
            showConfirmButton: true,
        });
    }
}

// Demand Table Tab Sagas

function* fetchForecastFilterDropdownsSaga() {
    console.log("Dropdown saga triggered");
    try {
        const response = yield call(services.getForecastFilterDropdowns);
        console.log("API RESPONSE:", response);

        yield put({
            type: types.FETCH_FORECAST_FILTER_DROPDOWNS_SUCCESS,
            payload: response.values
        });

    } catch (error) {
        yield put({
            type: types.FETCH_FORECAST_FILTER_DROPDOWNS_FAILURE,
            error: error.message
        });
    }
}


function* watchForecastSaga() {
    yield takeLatest(types.FETCH_FORECAST_REPORTS_TAB_REQUEST, fetchForecastReportsTabSaga);
    yield takeLatest(types.DELETE_FORECAST_REPORTS_REQUEST, deleteForecastReportSaga);
    yield takeLatest(types.FETCH_FORECAST_NOTES_EDIT_REQUEST, fetchForecastNotesEditSaga);
    yield takeLatest(types.FETCH_FORECAST_NOTES_SAVE_REQUEST, fetchForecastNotesSaveSaga);
    yield takeLatest(types.GET_ALL_FORECASTING_DEMAND_DROPDOWNS_REQUEST, fetchAllForecastingDemandDropdownsSaga);
    yield takeLatest(types.GET_FORECASTING_DEMAND_BY_ID_WITH_PAGINATION_REQUEST, fetchForecastingDemandByIdWithPaginationSaga);
    yield takeLatest(types.GET_FORECAST_DEMAND_SITE_PRIORITY_DRILLTHROUGH_REQUEST, fetchForecastDemandSitePriorityDrillthroughSaga);
    // yield takeLatest(types.SAVE_FORECASTING_DEMAND_REPORTS_REQUEST, saveForecastingDemandReportsSaga);
    yield takeLatest(types.FETCH_FORECAST_FILTER_DROPDOWNS_REQUEST, fetchForecastFilterDropdownsSaga);

}

export default watchForecastSaga;